import React, { useState, useEffect, useCallback } from 'react';
import { Search, Clock, Star, Zap, FileText, Settings, Download, Users } from 'lucide-react';

interface Command {
  id: string;
  title: string;
  description?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords: string[];
  category: 'navigation' | 'action' | 'recent' | 'favorite';
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (view: string) => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  onNavigate,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentCommands, setRecentCommands] = useState<string[]>([]);

  // Define all available commands
  const commands: Command[] = [
    {
      id: 'nav-timeline',
      title: 'Go to Timeline',
      description: 'View project timeline',
      icon: <Clock className="w-4 h-4" />,
      action: () => onNavigate('timeline'),
      keywords: ['timeline', 'gantt', 'schedule'],
      category: 'navigation',
    },
    {
      id: 'nav-analytics',
      title: 'Go to Analytics',
      description: 'View analytics dashboard',
      icon: <FileText className="w-4 h-4" />,
      action: () => onNavigate('analytics'),
      keywords: ['analytics', 'stats', 'reports', 'metrics'],
      category: 'navigation',
    },
    {
      id: 'nav-worklog',
      title: 'Go to Work Log',
      description: 'Track your work time',
      icon: <Clock className="w-4 h-4" />,
      action: () => onNavigate('worklog'),
      keywords: ['work', 'log', 'time', 'track'],
      category: 'navigation',
    },
    {
      id: 'nav-settings',
      title: 'Go to Settings',
      description: 'Configure app settings',
      icon: <Settings className="w-4 h-4" />,
      action: () => onNavigate('settings'),
      keywords: ['settings', 'config', 'preferences'],
      category: 'navigation',
    },
    {
      id: 'action-export-pdf',
      title: 'Export to PDF',
      description: 'Download current view as PDF',
      icon: <Download className="w-4 h-4" />,
      action: () => console.log('Export PDF'),
      keywords: ['export', 'pdf', 'download', 'print'],
      category: 'action',
    },
    {
      id: 'action-export-csv',
      title: 'Export to CSV',
      description: 'Download data as spreadsheet',
      icon: <Download className="w-4 h-4" />,
      action: () => console.log('Export CSV'),
      keywords: ['export', 'csv', 'excel', 'spreadsheet'],
      category: 'action',
    },
    {
      id: 'action-share',
      title: 'Share with Team',
      description: 'Share current view',
      icon: <Users className="w-4 h-4" />,
      action: () => console.log('Share'),
      keywords: ['share', 'team', 'collaborate'],
      category: 'action',
    },
  ];

  // Filter commands based on search
  const filteredCommands = commands.filter((cmd) => {
    if (!search) return true;
    
    const searchLower = search.toLowerCase();
    return (
      cmd.title.toLowerCase().includes(searchLower) ||
      cmd.description?.toLowerCase().includes(searchLower) ||
      cmd.keywords.some(k => k.includes(searchLower))
    );
  });

  // Sort: recent first, then alphabetically
  const sortedCommands = [...filteredCommands].sort((a, b) => {
    const aRecent = recentCommands.includes(a.id);
    const bRecent = recentCommands.includes(b.id);
    if (aRecent && !bRecent) return -1;
    if (!aRecent && bRecent) return 1;
    return a.title.localeCompare(b.title);
  });

  // Execute selected command
  const executeCommand = useCallback((command: Command) => {
    command.action();
    
    // Add to recent commands
    setRecentCommands(prev => {
      const newRecent = [command.id, ...prev.filter(id => id !== command.id)].slice(0, 5);
      localStorage.setItem('recentCommands', JSON.stringify(newRecent));
      return newRecent;
    });
    
    onClose();
  }, [onClose]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < sortedCommands.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
          break;
        case 'Enter':
          e.preventDefault();
          if (sortedCommands[selectedIndex]) {
            executeCommand(sortedCommands[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, sortedCommands, executeCommand, onClose]);

  // Load recent commands
  useEffect(() => {
    const stored = localStorage.getItem('recentCommands');
    if (stored) {
      setRecentCommands(JSON.parse(stored));
    }
  }, []);

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl glass-heavy rounded-apple-2xl shadow-apple-modal overflow-hidden animate-in slide-in-from-top-4 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 p-4 border-b border-system-separator">
          <Search className="w-5 h-5 text-system-label-tertiary flex-shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Type a command or search..."
            className="flex-1 apple-body bg-transparent text-system-label outline-none placeholder-system-label-tertiary"
            autoFocus
          />
          <kbd className="px-2 py-1 apple-caption2 text-system-label-tertiary bg-system-fill rounded-apple">
            ⌘K
          </kbd>
        </div>

        {/* Command List */}
        <div className="max-h-96 overflow-y-auto apple-scrollbar">
          {sortedCommands.length === 0 ? (
            <div className="p-8 text-center">
              <p className="apple-body text-system-label-secondary">
                No commands found
              </p>
            </div>
          ) : (
            <div className="p-2">
              {sortedCommands.map((command, index) => {
                const isRecent = recentCommands.includes(command.id);
                const isSelected = index === selectedIndex;

                return (
                  <button
                    key={command.id}
                    onClick={() => executeCommand(command)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`
                      w-full flex items-center gap-3 p-3 rounded-apple-lg
                      transition-all duration-150
                      ${isSelected 
                        ? 'bg-apple-blue/10 border-l-2 border-apple-blue' 
                        : 'hover:bg-system-fill/50'
                      }
                    `}
                  >
                    <div className={`
                      flex-shrink-0 w-8 h-8 rounded-apple flex items-center justify-center
                      ${isSelected ? 'bg-apple-blue text-white' : 'bg-system-fill text-system-label-secondary'}
                    `}>
                      {command.icon}
                    </div>
                    
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <p className="apple-callout text-system-label font-medium">
                          {command.title}
                        </p>
                        {isRecent && (
                          <Clock className="w-3 h-3 text-system-label-tertiary" />
                        )}
                      </div>
                      {command.description && (
                        <p className="apple-caption1 text-system-label-secondary">
                          {command.description}
                        </p>
                      )}
                    </div>

                    {isSelected && (
                      <kbd className="px-2 py-1 apple-caption2 text-system-label-tertiary bg-system-fill rounded-apple">
                        ↵
                      </kbd>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Tips */}
        <div className="p-3 border-t border-system-separator bg-system-surface-secondary">
          <div className="flex items-center justify-between apple-caption1 text-system-label-tertiary">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-system-fill rounded">↑↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-system-fill rounded">↵</kbd>
                Select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-system-fill rounded">esc</kbd>
                Close
              </span>
            </div>
            <span>{sortedCommands.length} commands</span>
          </div>
        </div>
      </div>
    </div>
  );
};
